import { useState, useMemo, useEffect } from "react";
import { useDebounce } from "../../../hooks/useDebounce";

/**
 * Hook dedicado à busca e manipulação da lista mista de alunos na Avaliação
 * (alunos originais da turma + alunos adicionados manualmente na sessão).
 */
export const useEvaluationSearch = ({ classMembers, allMembers, classId }) => {
    const [extraMembers, setExtraMembers] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [excludedMemberIds, setExcludedMemberIds] = useState(() => new Set());

    const debouncedSearch = useDebounce(searchText, 400);

    // Reset view specific states when class changes
    useEffect(() => {
        setExtraMembers([]);
        setSearchText("");
        setExcludedMemberIds(new Set());
    }, [classId]);

    // Mix de clientes: turma + adicionados manualmente
    const combinedMembers = useMemo(() => {
        const base = Array.isArray(classMembers) ? classMembers : [];
        const extra = Array.isArray(extraMembers) ? extraMembers : [];
        const map = new Map();
        base.forEach(s => {
            if (s?.id == null) return;
            map.set(String(s.id), s);
        });
        extra.forEach(s => {
            if (s?.id == null) return;
            const key = String(s.id);
            if (!map.has(key)) map.set(key, s);
        });
        return Array.from(map.values());
    }, [classMembers, extraMembers]);

    // Clientes visíveis (sem os excluídos)
    const evaluationMembers = useMemo(() => {
        const base = Array.isArray(combinedMembers) ? combinedMembers : [];
        if (!excludedMemberIds || excludedMemberIds.size === 0) return base;
        return base.filter(s => !excludedMemberIds.has(String(s.id)));
    }, [combinedMembers, excludedMemberIds]);

    // Candidatos para adicionar (buscando no pool de membros com debounce)
    const addCandidates = useMemo(() => {
        const q = (debouncedSearch || "").trim().toLowerCase();
        if (!q || q.length < 2) return [];
        const existingIds = new Set((combinedMembers || []).map(s => String(s.id)));
        return (allMembers || [])
            .filter(m => !existingIds.has(String(m.id)))
            .filter(m => {
                const name = (m.fullName || m.name || "").toLowerCase();
                return name.includes(q);
            })
            .map(m => ({
                id: m.id,
                idMember: m.id,
                name: m.fullName || m.name || "Membro",
                photo: m.photoUrl || null,
                phone: m.phone || null,
                email: m.email || null,
                memberNumber: m.memberNumber || "",
            }))
            .slice(0, 8);
    }, [allMembers, combinedMembers, debouncedSearch]);

    const showNoAutocompleteResults = useMemo(() => {
        const q = (debouncedSearch || "").trim();
        if (!q || q.length < 2) return false;
        return addCandidates.length === 0;
    }, [debouncedSearch, addCandidates.length]);

    const toggleExcludeMember = (idMember) => {
        const key = String(idMember);
        setExcludedMemberIds(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleAddMember = (member) => {
        const sId = String(member.id);
        setExtraMembers(prev => {
            const next = Array.isArray(prev) ? prev.slice() : [];
            if (!next.some(x => String(x.id) === sId)) next.push(member);
            return next;
        });
        setExcludedMemberIds(prev => {
            const next = new Set(prev);
            next.delete(sId);
            return next;
        });
        setSearchText("");
    };

    return {
        searchText,
        setSearchText,
        combinedMembers,
        evaluationMembers,
        addCandidates,
        showNoAutocompleteResults,
        toggleExcludeMember,
        handleAddMember,
        excludedMemberIds
    };
};
